import { inject, Injectable } from '@angular/core';
import { type Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import type { Pagination } from '../../shared/models/pagination';
import { serializeQueryParams } from '../../shared/utils/serialize-query-params';
import type { SessionFilter } from './model/session-filter';
import type { SessionListDTO } from './model/session-list.dto';
import type { SessionGetDTO } from './model/session-get.dto';
import type { SessionPostPutDTO } from './model/session-post-put.dto';
import { sanitizePayload } from '../../shared/utils/sanitize-payload';
import type { Id } from '../../shared/models/id';

@Injectable()
export class SessionService {
  private readonly apiUrl = environment.apiUrl;
  private readonly http = inject(HttpClient);

  getAll(projectId: string, filter: SessionFilter): Observable<Pagination<SessionListDTO>> {
    return this.http.get<Pagination<SessionListDTO>>(`${this.apiUrl}/projects/${projectId}/sessions`, {
      params: serializeQueryParams(filter),
    });
  }

  get(projectId: string, sessionId: string): Observable<SessionGetDTO> {
    return this.http.get<SessionGetDTO>(`${this.apiUrl}/projects/${projectId}/sessions/${sessionId}`);
  }

  create(projectId: string, session: SessionPostPutDTO): Observable<Id> {
    return this.http.post<Id>(`${this.apiUrl}/projects/${projectId}/sessions`, sanitizePayload(session));
  }

  update(projectId: string, sessionId: string, session: SessionPostPutDTO): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/projects/${projectId}/sessions/${sessionId}`, sanitizePayload(session));
  }

  delete(projectId: string, sessionId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/projects/${projectId}/sessions/${sessionId}`);
  }

  importRecording(projectId: string, sessionId: string, zip: File): Observable<void> {
    const formData = new FormData();
    formData.append('file', zip, zip.name);

    return this.http.post<void>(
      `${this.apiUrl}/projects/${projectId}/sessions/${sessionId}/import-recording`,
      formData,
    );
  }
}
